import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import {
	Root,
	SideNavToggleButton,
	TopNav,
	TopNavEnd,
	TopNavMiddle,
	TopNavStart,
} from '@atlaskit/navigation-system';
import { Banner } from '@atlaskit/navigation-system/layout/banner';
import { Help } from '@atlaskit/navigation-system/top-nav-items';
import { fg } from '@atlaskit/platform-feature-flags';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation';
import type { GlobalSideNavigationProps } from '@atlassian/global-side-navigation/types';
import Ribbon from '@atlassian/navigation-ribbon';
import type { NavigationRibbonProps } from '@atlassian/navigation-ribbon/types';

import { IsRibbonEnabledProvider } from '../../common/ui/ribbon-rollout/is-ribbon-enabled-provider';

import { messages } from './messages';
import type { BannerProps, GlobalRootProps, TopNavProps } from './types';

type FallbackTopNavProps = Partial<TopNavProps> & {
	appLabel: string | undefined;
	isAnonymous: boolean;
};

/**
 * Renders the default fallback top nav items when no children are supplied.
 *
 * Isolated into its own component so that useIntl() is only called on the path
 * that actually formats messages. Consumers that pass children (e.g. Jira via
 * TopNavSlot) never instantiate this component and therefore have no IntlProvider
 * requirement.
 */
function FallbackTopNavItems({
	appLabel,
	isAnonymous,
}: {
	appLabel: string | undefined;
	isAnonymous: boolean;
}) {
	const { formatMessage } = useIntl();
	return (
		<>
			<TopNavStart
				sideNavToggleButton={
					<SideNavToggleButton
						collapseLabel={formatMessage(messages.collapseSidebar)}
						expandLabel={formatMessage(messages.expandSidebar)}
					/>
				}
			>
				<div>{formatMessage(messages.logo)}</div>
				<div>{appLabel}</div>
			</TopNavStart>
			<TopNavMiddle>
				<div>{formatMessage(messages.search)}</div>
				<div>{formatMessage(messages.universalCreate)}</div>
			</TopNavMiddle>
			<TopNavEnd>
				<div>{formatMessage(messages.editionAwareness)}</div>
				{!isAnonymous && <div>{formatMessage(messages.conversationAssistant)}</div>}
				{!isAnonymous && <div>{formatMessage(messages.notifications)}</div>}
				<Help label={formatMessage(messages.help)} />
				<div>{formatMessage(messages.settings)}</div>
				<div>{formatMessage(messages.profile)}</div>
			</TopNavEnd>
		</>
	);
}

/**
 * Fallback top nav rendered when the consumer does not supply a TopNavSlot.
 *
 * Defined as a separate named component so that GlobalRoot itself has no
 * IntlProvider requirement. Consumers that supply their own TopNavSlot (the
 * vast majority) are completely unaffected. Only the fallback items path (used
 * in dev/testing when no children are passed) needs an IntlProvider in the tree.
 */
function FallbackTopNav({ appLabel, isAnonymous, ...wrapperProps }: FallbackTopNavProps) {
	return (
		// TODO: Change the ADS component to accept the sidebar width navx-3998
		<TopNav testId="global-root-topnav" {...wrapperProps}>
			{/* Jira will pass the children prop, and when GlobalTopNav comes along, it will simplify this substantially */}
			{wrapperProps.children ? (
				wrapperProps.children
			) : (
				<FallbackTopNavItems appLabel={appLabel} isAnonymous={isAnonymous} />
			)}
		</TopNav>
	);
}

/**
 * GlobalRoot will _progressively_ increase in capability.
 * Initially, it will care about the Banner, Ribbon, TopNav and SideNav.
 *
 * Hopefully the GlobalTopNav will simplify the TopNav slot.
 *
 * This means that apps *still* need to co-ordinate the right-side panel themselves,
 * which means the TopNav button "Help" needs to be specially-customizable by apps,
 * to participate in the co-ordination
 */
export function GlobalRoot(props: GlobalRootProps): React.JSX.Element {
	di(GlobalSideNavigation, TopNav, Ribbon);

	const {
		app,
		appLabel,
		children,
		isAnonymous = false,
		isRibbonEnabled = fg('navx-ribbon-rollout'),
		BannerSlot,
		RibbonSlot,
		SideNavSlot,
		TopNavSlot,
		...rootProps
	} = props;

	const BaseBanner = useCallback((wrapperProps: BannerProps) => {
		return <Banner {...wrapperProps} />;
	}, []);

	const BaseRibbon = useCallback((wrapperProps: Partial<NavigationRibbonProps>) => {
		return <Ribbon testId="global-root-ribbon" {...wrapperProps} />;
	}, []);

	const BaseTopNav = useCallback(
		(wrapperProps: Partial<TopNavProps>) => {
			return <FallbackTopNav appLabel={appLabel} isAnonymous={isAnonymous} {...wrapperProps} />;
		},
		[appLabel, isAnonymous],
	);

	const BaseSideNav = useCallback(
		(wrapperProps: Partial<GlobalSideNavigationProps>) => {
			return (
				<GlobalSideNavigation
					app={app}
					appLabel={appLabel}
					testId="global-root-sidenav"
					forYou={{}}
					items={{}}
					{...wrapperProps}
				/>
			);
		},
		[app, appLabel],
	);

	return (
		<IsRibbonEnabledProvider value={isRibbonEnabled}>
			<Root {...rootProps}>
				{BannerSlot && <BannerSlot Banner={BaseBanner} />}
				{!isAnonymous &&
					isRibbonEnabled &&
					RibbonSlot !== null &&
					(RibbonSlot ? <RibbonSlot Ribbon={BaseRibbon} /> : <BaseRibbon />)}
				{TopNavSlot !== null && (TopNavSlot ? <TopNavSlot TopNav={BaseTopNav} /> : <BaseTopNav />)}
				{SideNavSlot !== null &&
					(SideNavSlot ? <SideNavSlot SideNav={BaseSideNav} /> : <BaseSideNav />)}
				{children}
			</Root>
		</IsRibbonEnabledProvider>
	);
}
